import { z } from "zod";
import { EvidenceSourceRefSchema, EvidenceTimeScopeSchema } from "./evidence";
import { ScreeningResultSchema } from "./screening-result";
export { ScreeningResultSchema, screeningResultSummary, type ScreeningResult } from "./screening-result";
export const MarketEvidenceSubmissionSchema = z.object({
  type: z.literal("market_evidence"), submissionId: z.string().uuid(),
  kind: z.enum(["demand", "competition", "review_pain", "price_band"]),
  platform: z.string().trim().min(1).max(80), market: z.string().trim().min(1).max(80),
  category: z.string().trim().min(1).max(160),
  periodStart: z.string().date(), periodEnd: z.string().date(),
  sourceType: z.enum(["sales", "trend", "third_party", "product", "reviews"]),
  sourceUrl: z.string().url().max(2048).refine(value => { try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password && ![...url.searchParams.keys()].some(key => /token|password|secret|authorization|session|api.?key/i.test(key)); } catch { return false; } }, "请填写不含账号密码或访问令牌的 HTTPS 来源地址"),
  content: z.string().trim().min(1).max(8000),
  origin: z.enum(["manual", "page", "file"]),
  collectedAt: z.string().datetime(), collectionTaskId: z.string().uuid().optional(),
}).strict();
export type MarketEvidenceSubmission = z.infer<typeof MarketEvidenceSubmissionSchema>;
export const MarketVerificationAnalysisSchema = z.object({
  status: z.enum(["supported", "insufficient", "conflict"]),
  summary: z.string().min(1).max(600),
  quotes: z.array(z.string().min(1).max(500)).min(1).max(6),
  missing: z.array(z.string().min(1).max(200)).max(8),
}).strict();
export const MarketVerificationRecordSchema = z.object({
  submission: MarketEvidenceSubmissionSchema,
  status: z.enum(["supported", "insufficient", "conflict"]),
  summary: z.string().min(1).max(600), quotes: z.array(z.string().max(500)).max(6),
  missing: z.array(z.string().max(200)).max(8), checkedAt: z.string().datetime(),
}).strict();
export type MarketVerificationRecord = z.infer<typeof MarketVerificationRecordSchema>;

/* -------------------------------------------------------------------------- */
/* V2：timeScope、来源/目标分离、逐条引用（§8.2、§8.3、§14）                     */
/* -------------------------------------------------------------------------- */

/**
 * V2 与 V1 明确分开：V1 用 `periodStart/periodEnd` 两个必填日期，
 * V2 用 `timeScope` 保留"过去 30 天"这类原始描述，**不编造绝对起止**。
 * 两者 `type` 不同，因此不会互相静默解析成功。
 */
export const MarketEvidenceSubmissionV2Schema = z.object({
  type: z.literal("market_evidence_v2"),
  submissionId: z.string().uuid(),
  kind: z.enum(["demand", "competition", "review_pain", "price_band"]),
  /** 目标平台/国家属于本次任务。 */
  targetPlatform: z.string().trim().min(1).max(80),
  targetMarket: z.string().trim().min(1).max(80),
  category: z.string().trim().min(1).max(160),
  /** 来源平台/国家属于本批资料；与目标分开校验，不互相冒充。 */
  sourcePlatform: z.string().trim().min(1).max(80),
  sourceMarket: z.string().trim().min(1).max(80),
  timeScope: EvidenceTimeScopeSchema,
  sourceType: z.enum(["sales", "trend", "third_party", "product", "reviews"]),
  sourceUrl: z.string().url().max(2048).refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && !url.username && !url.password
        && ![...url.searchParams.keys()].some((key) => /token|password|secret|authorization|session|api.?key/i.test(key));
    } catch {
      return false;
    }
  }, "请填写不含账号密码或访问令牌的 HTTPS 来源地址"),
  /** 界面语言、原始关键词与目标市场语言分别记录（§8.2）。 */
  language: z.object({
    interface: z.string().max(35).optional(),
    originalQuery: z.string().max(160).optional(),
    executedQuery: z.string().max(160).optional(),
    targetMarketLanguage: z.string().max(35).optional(),
  }).strict().optional(),
  filterState: z.string().max(300).optional(),
  content: z.string().trim().min(1).max(8000),
  /** 页面表达式取数时逐项带出处；手填/导入可以为空。 */
  // 两轮各20商品，每个最终字段（名称/链接/价格/销量）可能来自不同可见快照。
  // 保留最多40×4条真实出处，不能为旧60条限制静默丢失销量来源。
  sourceRefs: z.array(EvidenceSourceRefSchema).max(160).default([]),
  origin: z.enum(["manual", "page", "file", "acquisition_session"]),
  collectedAt: z.string().datetime(),
  collectionTaskId: z.string().uuid().optional(),
  /** 本地取数会话标识：只用于关联，不授予执行权限。 */
  acquisitionSessionId: z.string().min(1).max(160).optional(),
  screeningResult: ScreeningResultSchema.optional(),
}).strict().superRefine((value, context) => {
  if (value.screeningResult && (value.screeningResult.kind !== value.kind || value.origin !== "acquisition_session")) {
    context.addIssue({ code: "custom", path: ["screeningResult"], message: "固定链路结果必须与本次取数信号一致" });
  }
  // 同一批资料里目标与来源都写"马来西亚"没问题；但来源国家不能顶上目标国家的位置。
  if (!value.targetMarket.trim() || !value.sourceMarket.trim()) {
    context.addIssue({ code: "custom", path: ["sourceMarket"], message: "来源市场与目标市场必须分别给出" });
  }
  if (value.sourceRefs.length > 0 && value.origin !== "page" && value.origin !== "acquisition_session") {
    context.addIssue({ code: "custom", path: ["sourceRefs"], message: "只有页面取数来源才允许带结构化出处" });
  }
});
export type MarketEvidenceSubmissionV2 = z.infer<typeof MarketEvidenceSubmissionV2Schema>;

/** 逐字引文必须带出处引用；位置合法不能证明 OCR 正确，所以只做逐字核对。 */
export const MarketVerificationQuoteV2Schema = z.object({
  text: z.string().min(1).max(500),
  sourceRef: z.string().min(1).max(160),
}).strict();
export type MarketVerificationQuoteV2 = z.infer<typeof MarketVerificationQuoteV2Schema>;

/**
 * 兴趣证据与购买证据分开：Google Trends 可以形成兴趣结论，
 * 但购买需求仍需成交类依据（§8.3）。
 */
export const MarketDemandEvidenceSplitSchema = z.object({
  interest: z.array(MarketVerificationQuoteV2Schema).max(8).default([]),
  purchase: z.array(MarketVerificationQuoteV2Schema).max(8).default([]),
}).strict();
export type MarketDemandEvidenceSplit = z.infer<typeof MarketDemandEvidenceSplitSchema>;

export const MarketVerificationRecordV2Schema = z.object({
  submission: MarketEvidenceSubmissionV2Schema,
  status: z.enum(["supported", "insufficient", "conflict"]),
  summary: z.string().min(1).max(600),
  quotes: z.array(MarketVerificationQuoteV2Schema).max(6),
  missing: z.array(z.string().max(200)).max(8),
  /**
   * 需求类资料把"兴趣证据"与"购买证据"分开记录。
   *
   * **口径变更（用户确认，2026-09-17）**：搜索兴趣（例如 Google Trends）在本产品里
   * **代表市场需求**，因此**不再要求**"必须有购买证据"才能判 `supported`。
   * 分开记录这条**保留**——它是有用的信息（有成交数据时更强），只是不再是判定门槛。
   * 原先这里有一条 `superRefine` 会对"supported 但没有 purchase"直接报错，已删除。
   */
  demandEvidence: MarketDemandEvidenceSplitSchema.optional(),
  checkedAt: z.string().datetime(),
}).strict();
export type MarketVerificationRecordV2 = z.infer<typeof MarketVerificationRecordV2Schema>;

/**
 * 旧记录**只读**映射为 range，用于展示与兼容读取。刻意叫 readOnly：
 * 这条路径不允许回写 V1 记录，V2 也不能被降级成假起止日期（§14）。
 */
export function mapMarketEvidenceV1TimeScopeReadOnly(submission: MarketEvidenceSubmission): { kind: "range"; start: string; end: string } {
  return { kind: "range", start: submission.periodStart, end: submission.periodEnd };
}

/** V2 记录是否可以被 V1 读取路径安全展示：只有 range 才能给出起止。 */
export function marketEvidenceV2ReadOnlySummary(record: MarketVerificationRecordV2): {
  status: "supported" | "insufficient" | "conflict";
  timeScope: MarketEvidenceSubmissionV2["timeScope"];
  hasAbsoluteRange: boolean;
} {
  return {
    status: record.status,
    timeScope: record.submission.timeScope,
    hasAbsoluteRange: record.submission.timeScope.kind === "range",
  };
}
